import { convertScratchToPython, convertScratchToPythonMultiFile, getStandalonePythonCode } from "./scratchToPython.js";
import { convertScratchToJs, convertScratchToJsMultiFile, getStandaloneJsCode } from "./scratchToJs.js";
import { convertScratchToArduino } from "./scratchToArduino.js";
import { convertScratchToCpp } from "./scratchToCpp.js";
import { convertPythonToTarget, convertJsToTarget } from "./pythonToTargets.js";

/**
 * Universal Practido Multi-Language Transpiler Dispatcher
 * @param {Object} params
 * @param {string} params.sourceEnv - "scratch" | "python" | "javascript" | "scratch_jr"
 * @param {string} params.targetLang - "python" | "javascript" | "arduino" | "cpp"
 * @param {string|Object} params.sourceCode - Python / JS code string or Scratch JSON project
 * @param {string|null} [params.targetName] - Optional target sprite name or "Stage" for single-file isolation
 * @returns {string} Transpiled output code in target language
 */
export const transpileCode = ({ sourceEnv, targetLang, sourceCode, targetName = null }) => {
  try {
    if (sourceEnv === "scratch" || sourceEnv === "scratch_jr" || sourceEnv === "junior") {
      if (targetLang === "python") {
        return convertScratchToPython(sourceCode, targetName);
      } else if (targetLang === "javascript") {
        return convertScratchToJs(sourceCode, targetName);
      } else if (targetLang === "arduino") {
        return convertScratchToArduino(sourceCode);
      } else if (targetLang === "cpp") {
        return convertScratchToCpp(sourceCode);
      }
    }

    if (sourceEnv === "python") {
      if (targetLang === "python") {
        return sourceCode || "# Python Code";
      }
      return convertPythonToTarget(sourceCode, targetLang);
    }

    if (sourceEnv === "javascript") {
      if (targetLang === "javascript") {
        return sourceCode || "// JavaScript Code";
      }
      return convertJsToTarget(sourceCode, targetLang);
    }

    return `# Source environment ${sourceEnv} or target ${targetLang} is not supported.`;
  } catch (err) {
    return `# Error during transpilation: ${err.message}`;
  }
};

/**
 * Multi-file transpiler for Scratch project JSON
 * Produces Stage.py/js + <SpriteName>.py/js files
 */
export const transpileScratchMultiFile = ({ targetLang = "python", sourceCode }) => {
  if (targetLang === "python") {
    return convertScratchToPythonMultiFile(sourceCode);
  } else if (targetLang === "javascript") {
    return convertScratchToJsMultiFile(sourceCode);
  }
  return { files: [], activeTarget: "Stage" };
};

/**
 * Packages code into a self-contained offline executable format for 1-click downloads
 */
export const getStandaloneDownloadCode = ({ sourceEnv, targetLang, code }) => {
  if (!code) return "";
  const isFromScratch =
    sourceEnv === "scratch" ||
    sourceEnv === "scratch_jr" ||
    sourceEnv === "junior" ||
    code.includes("PractidoSprite");

  if (targetLang === "python") {
    if (isFromScratch || code.includes(".move(") || code.includes(".say(") || code.includes(".turn_right(")) {
      return getStandalonePythonCode(code);
    }
    return code;
  }
  if (targetLang === "javascript") {
    if (isFromScratch || code.includes(".move(") || code.includes(".say(") || code.includes(".turnRight(")) {
      return getStandaloneJsCode(code);
    }
    return code;
  }
  return code;
};
